import { useRef, useState, type ChangeEvent } from 'react';
import Btn from './ui/Btn';
import { ApiError, api } from '../lib/api';
import { toast } from '../stores/toaster';

type JowImportReport = {
  case_id: string | null;
  root_label: string | null;
  manifest_pieces: number;
  zip_pdfs: number;
  imported: number;
  duplicates: number;
  failed: number;
  complete: boolean;
  manifest_without_file: { order: number; label: string | null; path: string | null }[];
};

type Props = {
  dossierId: string;
  onDone?: () => void;
};

/**
 * Importe dans le dossier un ZIP produit par le userscript JustOnWeb (M24).
 * Affiche un rapport de complétude : importées / doublons / échecs, et alerte
 * si des pièces du manifeste sont absentes du ZIP (signal de troncature).
 */
export default function ImportJowZipButton({ dossierId, onDone }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  const trigger = () => {
    if (!busy) inputRef.current?.click();
  };

  const onFileChosen = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.postForm<JowImportReport>(
        `/dossiers/${dossierId}/import-jow-zip`,
        fd
      );
      const base =
        `${r.imported} pièce${r.imported > 1 ? 's' : ''} importée${r.imported > 1 ? 's' : ''}` +
        (r.duplicates > 0 ? ` · ${r.duplicates} doublon${r.duplicates > 1 ? 's' : ''}` : '') +
        (r.failed > 0 ? ` · ${r.failed} échec${r.failed > 1 ? 's' : ''}` : '');
      if (r.complete) {
        toast.success(`${base} — dossier complet (${r.zip_pdfs}/${r.manifest_pieces} pièces).`);
      } else {
        const miss = r.manifest_without_file.length;
        toast.error(
          `${base} — ⚠️ INCOMPLET : ${miss} pièce${miss > 1 ? 's' : ''} du manifeste ` +
            `absente${miss > 1 ? 's' : ''} du ZIP. Relance « Tout télécharger » sur JustOnWeb.`
        );
      }
      onDone?.();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept=".zip"
        onChange={(e) => void onFileChosen(e)}
        className="hidden"
      />
      <Btn
        variant="secondary"
        size="sm"
        onClick={trigger}
        disabled={busy}
        title="Importe un ZIP JustOnWeb (userscript M24) directement dans ce dossier"
      >
        {busy ? 'Import JoW…' : '📦 Importer un ZIP JustOnWeb'}
      </Btn>
    </>
  );
}
